import React from "react";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Stack,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";

const severityOf = (preview) => {
  if (!preview) return "info";
  if (preview.error || preview.quality === "empty") return "error";
  if (preview.quality === "low") return "warning";
  if (preview.quality === "ok") return "success";
  return "info";
};

export function knowledgeQualityChipColor(quality) {
  if (quality === "ok") return "success";
  if (quality === "low") return "warning";
  if (quality === "empty") return "error";
  return "default";
}

export function knowledgeQualityLabel(t, quality) {
  if (quality === "ok") return t("agentsListPage.knowledgePreviewQualityOk");
  if (quality === "low") return t("agentsListPage.knowledgePreviewQualityLow");
  if (quality === "empty") return t("agentsListPage.knowledgePreviewQualityEmpty");
  return t("agentsListPage.knowledgePreviewQualityUnknown");
}

/**
 * Dialog com texto extraído + alerta de qualidade (vazio / pouco relevante / ok).
 */
export default function AgentKnowledgeExtractDialog({
  open,
  onClose,
  loading,
  error,
  preview,
  title,
}) {
  const { t } = useTranslation("settings");
  const severity = severityOf(preview);

  return (
    <Dialog open={open} onClose={() => !loading && onClose?.()} fullWidth maxWidth="md">
      <DialogTitle>{title || t("agentsListPage.knowledgePreviewTitle")}</DialogTitle>
      <DialogContent dividers>
        {loading && (
          <Stack spacing={1.5} alignItems="center" sx={{ py: 3 }}>
            <CircularProgress size={28} />
            <Typography variant="body2" color="text.secondary">
              {t("agentsListPage.knowledgePreviewLoading")}
            </Typography>
          </Stack>
        )}
        {!loading && error && <Alert severity="error">{error}</Alert>}
        {!loading && !error && preview && (
          <Stack spacing={1.5}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <Chip
                size="small"
                color={knowledgeQualityChipColor(preview.quality)}
                label={knowledgeQualityLabel(t, preview.quality)}
              />
              <Typography variant="caption" color="text.secondary">
                {t("agentsListPage.knowledgePreviewStats", {
                  chars: preview.charCount ?? 0,
                  words: preview.wordCount ?? 0,
                })}
              </Typography>
              {preview.truncated && (
                <Chip size="small" variant="outlined" label={t("agentsListPage.knowledgePreviewTruncated")} />
              )}
            </Stack>

            {(preview.reason || preview.error) && (
              <Alert severity={severity} sx={{ py: 0.75 }}>
                {preview.error || preview.reason}
              </Alert>
            )}

            {preview.sample ? (
              <Box
                component="pre"
                sx={{
                  m: 0,
                  p: 1.5,
                  borderRadius: 1,
                  bgcolor: "action.hover",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                  fontFamily: "inherit",
                  fontSize: 13,
                  lineHeight: 1.45,
                  maxHeight: 420,
                  overflow: "auto",
                }}
              >
                {preview.sample}
              </Box>
            ) : (
              !preview.error && (
                <Typography variant="body2" color="text.secondary">
                  {t("agentsListPage.knowledgePreviewNoSample")}
                </Typography>
              )
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          {t("agentsListPage.knowledgePreviewClose")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
